import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTenant } from "@/core/tenant/TenantProvider";
import { useLogout, useSession } from "@/core/auth/useSession";
import { hasRole } from "@/core/auth/types";
import { ThemeToggle } from "@/core/theme";
import { NotificationOptInDialog } from "@/core/account/NotificationOptInDialog";
import { Button, cn } from "@/core/ui";
import { DuelBadge } from "@/core/duels/DuelBadge";

function navClass({ isActive }: { isActive: boolean }) {
  return cn("bt-nav-link", isActive && "bt-nav-link--active");
}

export function AppShell() {
  const tenant = useTenant();
  const { t, i18n } = useTranslation();
  const session = useSession();
  const logout = useLogout();
  const user = session.data?.user ?? null;
  return (
    <div className="bt-app">
      <header className="bt-app__header">
        <div className="bt-app__container">
          <NavLink to="/" className="bt-app__brand">
            {tenant.branding.logo_url ? (
              <img src={tenant.branding.logo_url} alt="" className="bt-app__logo" />
            ) : (
              <span className="bt-app__logo-placeholder" aria-hidden="true" />
            )}
            <span className="bt-app__brand-name">{tenant.branding.display_name}</span>
          </NavLink>
          <nav className="bt-app__nav" aria-label="main">
            <NavLink to="/" end className={navClass}>
              {t("nav.puzzles")}
            </NavLink>
            {user && tenant.enabled_games.length > 0 ? (
              <NavLink to="/games" className={navClass}>
                {t("nav.games")}
                <DuelBadge userId={user.id} />
              </NavLink>
            ) : null}
            {user ? (
              <NavLink to="/seasons" className={navClass}>
                {t("nav.seasons")}
              </NavLink>
            ) : null}
            {user ? (
              <NavLink to="/teams" className={navClass}>
                {t("nav.teams")}
              </NavLink>
            ) : null}
            {hasRole(user, "author", "reviewer", "tenant_admin") ? (
              <NavLink to="/backlog" className={navClass}>
                {t("nav.backlog")}
              </NavLink>
            ) : null}
            {hasRole(user, "tenant_admin") ? (
              <NavLink to="/admin" className={navClass}>
                {t("nav.admin")}
              </NavLink>
            ) : null}
            <ThemeToggle />
            <select
              aria-label={t("nav.language")}
              className="bt-app__picker"
              value={i18n.resolvedLanguage ?? "fr"}
              onChange={(e) => void i18n.changeLanguage(e.target.value)}
            >
              <option value="fr">FR</option>
              <option value="en">EN</option>
            </select>
            {user ? (
              <>
                <NavLink to="/account" className={navClass}>
                  {user.public_name}
                </NavLink>
                <Button variant="ghost" size="sm" onClick={() => logout.mutate()}>
                  {t("nav.logout")}
                </Button>
              </>
            ) : (
              <NavLink to="/auth/login" className={navClass}>
                {t("nav.login")}
              </NavLink>
            )}
          </nav>
        </div>
      </header>
      <main className="bt-app__container bt-app__main">
        <Outlet />
      </main>
      <footer className="bt-app__footer">{t("tenant.powered_by")}</footer>
      {user ? <NotificationOptInDialog /> : null}
    </div>
  );
}
